'use client';

import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LandingHeader, LandingFooter } from '../../../features/landing';

/**
 * TermsPageContent - Client component for the Terms of Service page
 *
 * Standard legal terms including:
 * - Free service, personal project
 * - No liability disclaimer
 * - User content responsibility
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function TermsPageContent() {
  const t = useTranslations('creator.terms');
  const locale = useLocale();

  // Format current date for "last updated"
  const lastUpdatedDate = new Date().toLocaleDateString(locale === 'he' ? 'he-IL' : locale === 'es' ? 'es-ES' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        {/* Page Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
          }}
        >
          {t('title')}
        </Typography>

        {/* Last Updated */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          {t('lastUpdated', { date: lastUpdatedDate })}
        </Typography>

        {/* Acceptance Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('acceptance')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('acceptanceText')}
          </Typography>
        </Paper>

        {/* The Service Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('service')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('serviceText')}
          </Typography>
        </Paper>

        {/* Disclaimer Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'warning.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'warning.200',
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('disclaimer')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('disclaimerText')}
          </Typography>
        </Paper>

        {/* User Content Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('content')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('contentText')}
          </Typography>
        </Paper>

        {/* Changes Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('changes')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('changesText')}
          </Typography>
        </Paper>

        <Divider sx={{ my: 4 }} />

        {/* Back to Home Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="large"
            sx={{ textTransform: 'none' }}
          >
            {t('backToHome')}
          </Button>
        </Box>
      </Container>

      <LandingFooter />
    </Box>
  );
}

export default TermsPageContent;
